import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, DatePicker, Popover } from "@shared/ui";
import selectStyles from "@shared/ui/Select/styles.module.scss";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { Check, ChevronDown } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import type { AccountingDateRange } from "../types";
import styles from "./MonthDateFilter.module.scss";

const RangePicker = DatePicker.RangePicker;
const CUSTOM_OPTION_VALUE = "__custom__";

const MONTH_OPTIONS_COUNT = 10;

interface MonthDateFilterProps {
  value?: AccountingDateRange;
  onChange: (range: AccountingDateRange | undefined) => void;
  placeholder?: string;
}

function getMonthOptions() {
  const options: { label: string; value: AccountingDateRange }[] = [];
  const now = dayjs();

  for (let i = 0; i < MONTH_OPTIONS_COUNT; i++) {
    const month = now.subtract(i, "month");
    const from = month.startOf("month").startOf("day").toDate();
    const to = month.endOf("month").endOf("day").toDate();
    options.push({
      label: month.format("MMMM YYYY"),
      value: { from, to },
    });
  }

  return options;
}

function rangesEqual(
  a?: AccountingDateRange,
  b?: AccountingDateRange
): boolean {
  if (!a || !b) return !a && !b;
  const aFrom = a.from ? dayjs(a.from).format("YYYY-MM-DD") : "";
  const aTo = a.to ? dayjs(a.to).format("YYYY-MM-DD") : "";
  const bFrom = b.from ? dayjs(b.from).format("YYYY-MM-DD") : "";
  const bTo = b.to ? dayjs(b.to).format("YYYY-MM-DD") : "";
  return aFrom === bFrom && aTo === bTo;
}

export function MonthDateFilter({
  value,
  onChange,
  placeholder = "Month",
}: MonthDateFilterProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"list" | "custom">("list");
  const [customRange, setCustomRange] = useState<[Dayjs | null, Dayjs | null]>([
    value?.from ? dayjs(value.from) : null,
    value?.to ? dayjs(value.to) : null,
  ]);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const monthOptions = useMemo(() => getMonthOptions(), []);

  const options = useMemo(
    () => [
      ...monthOptions.map((opt) => ({
        label: opt.label,
        value: opt.value,
        optionValue: JSON.stringify({
          from: opt.value.from?.toISOString(),
          to: opt.value.to?.toISOString(),
        }),
      })),
      { label: "Custom", value: null, optionValue: CUSTOM_OPTION_VALUE },
    ],
    [monthOptions]
  );

  const currentData = useMemo(() => {
    if (!value?.from && !value?.to) {
      return { selectedRange: undefined, isCustom: false };
    }
    const matching = monthOptions.find((opt) => rangesEqual(opt.value, value));
    return {
      selectedRange: value,
      isCustom: !matching,
    };
  }, [value, monthOptions]);

  const getDisplayLabel = () => {
    if (!currentData.selectedRange) return placeholder;

    if (currentData.isCustom) {
      const { from, to } = currentData.selectedRange;
      if (from && to) {
        return `${dayjs(from).format("DD MMM YYYY")} - ${dayjs(to).format("DD MMM YYYY")}`;
      }
      if (from) return `${dayjs(from).format("DD MMM YYYY")}+`;
      if (to) return `≤${dayjs(to).format("DD MMM YYYY")}`;
      return "Custom";
    }

    const matching = monthOptions.find((opt) =>
      rangesEqual(opt.value, currentData.selectedRange)
    );
    return matching?.label ?? "Custom";
  };

  const getSelectedValue = () => {
    if (!currentData.selectedRange) return undefined;
    if (currentData.isCustom) return CUSTOM_OPTION_VALUE;
    const matching = monthOptions.find((opt) =>
      rangesEqual(opt.value, currentData.selectedRange)
    );
    return matching?.value
      ? JSON.stringify({
          from: matching.value.from?.toISOString(),
          to: matching.value.to?.toISOString(),
        })
      : CUSTOM_OPTION_VALUE;
  };

  const handleOptionClick = (opt: (typeof optionsWithClear)[number]) => {
    if (opt.optionValue === "__clear__") {
      handleClear();
      return;
    }
    if (opt.optionValue === CUSTOM_OPTION_VALUE) {
      setView("custom");
      setCustomRange([
        value?.from ? dayjs(value.from) : null,
        value?.to ? dayjs(value.to) : null,
      ]);
      return;
    }

    // Fixed month option
    const range = opt.value as AccountingDateRange;
    onChange(range);
    setOpen(false);
    setView("list");
  };

  const handleApplyCustom = () => {
    const from = customRange[0]?.startOf("day").toDate();
    const to = customRange[1]?.endOf("day").toDate();
    if (!from && !to) return;
    onChange({ from, to });
    setOpen(false);
    setView("list");
  };

  const handleCancelCustom = () => {
    setCustomRange([
      value?.from ? dayjs(value.from) : null,
      value?.to ? dayjs(value.to) : null,
    ]);
    setView("list");
  };

  const handleBack = () => {
    setView("list");
  };

  const handleClear = () => {
    onChange(undefined);
    setOpen(false);
    setView("list");
  };

  const hasActiveValue = currentData.selectedRange !== undefined;

  const optionsWithClear = useMemo(
    () =>
      hasActiveValue
        ? [
            ...options,
            { label: "Clear", value: null, optionValue: "__clear__" as const },
          ]
        : options,
    [options, hasActiveValue]
  );
  const selectedValue = getSelectedValue();

  const renderDropdownContent = () => (
    <div
      className={selectStyles.searchableDropdown}
      style={{ width: buttonRef.current?.offsetWidth || 400 }}
    >
      <div className={selectStyles.dropdownPanel} style={{ width: 400 }}>
        {view === "list" ? (
          <div className={selectStyles.dropdownBody}>
            <div className={selectStyles.optionsList}>
              {optionsWithClear.map((opt) => {
                const selected = selectedValue === opt.optionValue;
                const showChevron =
                  opt.optionValue === CUSTOM_OPTION_VALUE && !selected;
                return (
                  <div
                    key={opt.optionValue}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleOptionClick(opt)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleOptionClick(opt)
                    }
                    className={`${selectStyles.optionItem} ${
                      selected ? selectStyles.optionItemSelected : ""
                    }`}
                  >
                    <span className={selectStyles.optionLabel}>{opt.label}</span>
                    {selected && (
                      <Check size={14} className={selectStyles.checkIcon} />
                    )}
                    {showChevron && (
                      <RightOutlined size={14} className={styles.chevronIcon} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={selectStyles.dropdownBody}>
            <div className={styles.customView}>
              <div className={styles.customHeader}>
                <button
                  type="button"
                  onClick={handleBack}
                  className={styles.backButton}
                  aria-label="Back"
                >
                  <LeftOutlined size={16} />
                </button>
                <span className={styles.customTitle}>Custom Date Range</span>
              </div>
              <div className={styles.customInputs}>
                <RangePicker
                  value={customRange}
                  onChange={(dates) =>
                    setCustomRange([
                      dates?.[0] ?? null,
                      dates?.[1] ?? null,
                    ])
                  }
                  className={styles.rangePicker}
                  allowClear
                />
                <div className={styles.customActions}>
                  <Button
                    type="default"
                    onClick={handleCancelCustom}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleApplyCustom}
                    className={styles.applyButton}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.monthDateFilter}>
      <Popover
        content={renderDropdownContent()}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomLeft"
        overlayClassName={styles.popoverOverlay}
        getPopupContainer={() => document.body}
        destroyTooltipOnHide={false}
        autoAdjustOverflow={false}
        zIndex={1050}
      >
        <button
          ref={buttonRef}
          type="button"
          className={`${selectStyles.selectButton} ${selectStyles.selectButtonMiddle} ${
            hasActiveValue ? styles.selectButtonActive : styles.selectButtonDefault
          }`}
        >
          <span className={selectStyles.buttonText}>{getDisplayLabel()}</span>
          <ChevronDown
            size={14}
            className={`${selectStyles.chevron} ${
              hasActiveValue ? selectStyles.chevronActive : selectStyles.chevronDefault
            } ${open ? selectStyles.chevronOpen : ""}`}
          />
        </button>
      </Popover>
    </div>
  );
}
